// The PURE half of resolving a market from the URL (TRY-289).
//
// ## ⚠️ Why this is a separate module, and it is a bundle boundary rather than taste
//
// `src/proxy.ts` needs to extract the market slug from a path, and its own header says
// it "deliberately still does not touch the database; every request pays for this file,
// so it stays cheap". The first version of this work put the extraction beside
// `resolveTenantBySlug` in one module — so importing it from the proxy pulled
// `centralPrisma`, `@prisma/adapter-pg` and `pg` into the file that runs in front of
// every anonymous storefront page view.
//
// So: this half imports nothing but `routing`, and `market-slug.ts` is the half that
// queries. Exactly the split `src/lib/phone.ts` / `phone-normalize.ts` draws, for
// exactly the same reason, and CLAUDE.md's phone section says of that one: "that
// boundary is a bundle-size contract, not a style preference, and it is enforced by
// nothing." The same is true here. Check which half you are importing.

import { routing } from "@/i18n/routing";

/**
 * The header `src/proxy.ts` sets when a request names a market in its path.
 *
 * A header rather than a `params` read, for the same reason `x-tenant-host` is one:
 * server actions do not receive route params, and the panel's actions all need the
 * market. `getStoreContext` reads it once and every caller inherits the answer through
 * a signature that did not change.
 */
export const MARKET_SLUG_HEADER = "x-market-slug";

/**
 * The routes that live DIRECTLY under `/{locale}/manage/`, i.e. as siblings of the
 * `[market]` segment rather than inside it.
 *
 * ⚠️ **`marketSlugFromPathname` must exclude these, and the first version of it did
 * not.** It reasoned that `login` is in `RESERVED_SLUGS`, so no market could be called
 * that — which is true and **irrelevant**: the reserved set stops a market being
 * created* with the name, and does nothing about the extractor reading the segment as
 * one. So `/en/manage/login` set `x-market-slug=login`, `resolveTenantBySlug` returned
 * null, `getStoreContext` answered `no-tenant`, and the login page redirected to `/en`.
 * Measured: 307 to `/en` on a tenant host with a perfectly good session.
 *
 * That is the pathology this repo keeps paying for — a docblock asserting a reason that
 * does not hold — and it was caught by driving the URL rather than by reading the code.
 *
 * ⚠️ **One list, two consumers.** `RESERVED_SLUGS` in `create-market.ts` spreads this,
 * so a route added here is reserved at creation automatically. Adding a static child
 * without adding it here shadows one market's panel and nobody else's, silently, because
 * Next gives a static segment priority over a dynamic sibling.
 */
export const MANAGE_STATIC_SEGMENTS: readonly string[] = ["change-password"];

/**
 * Slugs a market may not take even though nothing under `/manage/` shadows them.
 *
 * ⚠️ **`login`, `forgot` and `reset` were in {@link MANAGE_STATIC_SEGMENTS} until TRY-289's
 * adversary pass, and by then they named nothing.** The three screens moved out from under
 * `/manage/` entirely — they are `/{locale}/login` and siblings now — leaving one real static
 * child, `change-password`. Three dead entries in that list was not merely untidy: every
 * consumer treats a member as "a legitimate market-less `/manage/…` path", so
 * `verify-panel.ts`'s href check **skipped** a leftover `/{locale}/manage/login` link instead
 * of reporting the dead link it is, and `marketHref` / `panelHref` declined to prefix one.
 * The gate built to catch exactly that class had a hole in it shaped like these three names.
 *
 * They stay reserved, for a reason that is no longer about shadowing: a market slugged
 * `login` makes `/en/manage/login` a real market panel that reads like the sign-in page, and
 * the root-level `/{locale}/login` route makes the confusion two clicks wide. Cheap to keep
 * out, and nothing needs them.
 *
 * ⚠️ Since TRY-497 this list is also the proxy's redirect set: `/{locale}/<name>` is sent
 * from every tenant host to the root domain for each name here. A name added for the
 * shadowing reason alone starts redirecting a first segment the storefront's `[pageSlug]`
 * might otherwise serve, so add here only what is a root-level identity route as well.
 */
export const RESERVED_MANAGE_LOOKALIKES: readonly string[] = [
	"login",
	"forgot",
	"reset",
	// TRY-398's public front door, `/{locale}/signup`, reserved for exactly the
	// look-alike reason above and NOT because anything shadows it — nothing under
	// `/manage/` is called `signup`, and the front door is a root-level sibling of
	// `login`. A market slugged `signup` would make `/en/manage/signup` a real panel
	// one segment away from the page that creates markets. Cheap to keep out.
	"signup",
	// The same reasoning covers the other two root-level pages: the market chooser
	// and invite acceptance.
	"markets",
	"invite",
	// The verify and unsubscribe links of the order email notifications: root-level, and
	// addressed to people outside every market.
	"notify",
];

/**
 * Pull the market slug out of a pathname, or `null`.
 *
 * Two shapes: `/{locale}/manage/{market}/…` for a page, and `/api/manage/{market}/…` for
 * the panel's own API routes, which are not localised. On the page shape the locale segment
 * is required — `localePrefix` is `"always"` in `routing.ts` — so a path whose first segment
 * is neither a known locale nor `api` names no market. Neither does `/manage/login` and its three siblings — see
 * {@link MANAGE_STATIC_SEGMENTS} for why that exclusion is explicit rather than implied
 * by the reserved-slug set.
 *
 * ⚠️ Pure and string-in/string-out, so the proxy can call it and a probe can call it
 * with a literal. The slug shape is re-spelled here rather than imported from
 * `fields.ts`, which pulls in zod.
 */
export function marketSlugFromPathname(pathname: string): string | null {
	const parts = pathname.split("/");
	// ⚠️ **TWO shapes, and missing the second one broke the panel on the root domain.**
	// `/api/manage/{market}/…` has no locale segment — an API route is not localised — so a
	// locale-only parser returned null for it. On a tenant host that was invisible, because
	// the market was still resolvable by HOST; on the root domain there is no host to fall
	// back to, so the panel shell's own poll answered **401 on every page** and
	// `verify-seeded.ts`'s console sweep is what said so. The two shapes are enumerated
	// rather than made flexible: a parser that accepted "market is the segment after
	// `manage`" anywhere would also read `/en/manage/login` as a market, which is the
	// defect the static-segment list below already exists to prevent.
	const [, first, second, third] = parts;
	const market =
		first === "api" && second === "manage"
			? third
			: first && (routing.locales as readonly string[]).includes(first) && second === "manage"
				? third
				: undefined;
	if (!market) return null;
	if (MANAGE_STATIC_SEGMENTS.includes(market)) return null;
	return /^[a-z][a-z0-9-]{1,40}$/.test(market) ? market : null;
}

/**
 * Does this pathname name one of the root-level identity routes (TRY-497)?
 *
 * The six are {@link RESERVED_MANAGE_LOOKALIKES} exactly, and reusing that list rather
 * than declaring a second one is the point: it already holds `login`, `forgot`, `reset`,
 * `signup`, `markets` and `invite`, so the set the proxy redirects and the set a market
 * may not be slugged cannot drift apart.
 *
 * ⚠️ **The locale segment is OPTIONAL here**, unlike in {@link marketSlugFromPathname}.
 * The proxy calls this BEFORE `intlMiddleware` has normalised the path, so `/signup`
 * arrives as often as `/en/signup` does.
 *
 * First-segment matching is also what keeps the shopper's own door out of it: the
 * storefront sign-in is `/{locale}/account/login`, whose first segment is `account`.
 */
export function isRootIdentityPathname(pathname: string): boolean {
	const [, first, second] = pathname.split("/");
	const segment = first && (routing.locales as readonly string[]).includes(first) ? second : first;
	return !!segment && RESERVED_MANAGE_LOOKALIKES.includes(segment);
}
